/**
 * Internal Node Component for React Flow
 *
 * Renders internal elements within the Smart Asset boundary.
 * Cyan/blue styling for the core logic components.
 */
import { memo, useState } from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import type { ValueFlowNodeData } from "../../../../utils/schemaToReactFlow";
import { useHoverContext } from "../HoverContext";
import { NodeHoverLabel } from "./NodeHoverLabel";
import { NodeMetadataTooltip } from "./NodeMetadataTooltip";
import { ObservableBadgesContainer } from "./ObservableBadge";

/**
 * Resolve spec_icon path to actual URL
 */
const resolveSpecIconUrl = (specIcon: string): string => {
  const filename = specIcon.split("/").pop();
  return `/assets/sa-elements/${filename}`;
};

/**
 * Default component icon SVG
 */
const ComponentIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="6" y="6" width="12" height="12" rx="2" />
    <path d="M9 2v4m6-4v4M9 18v4m6-4v4M2 9h4m12 0h4M2 15h4m12 0h4" />
  </svg>
);

/**
 * Treasury-specific icon
 */
const TreasuryIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="8" width="18" height="12" rx="2" />
    <path d="M3 12h18M7 8V6a2 2 0 012-2h6a2 2 0 012 2v2" />
    <circle cx="12" cy="14" r="2" />
  </svg>
);

/**
 * Get icon based on element position/role
 */
const getDefaultIcon = (position?: string) => {
  if (position?.includes("treasury")) return <TreasuryIcon />;
  return <ComponentIcon />;
};

export const InternalNodeRF = memo(({ id, data, selected }: NodeProps<ValueFlowNodeData>) => {
  const [imageError, setImageError] = useState(false);
  const [badgeError, setBadgeError] = useState(false);
  const { hoveredNodeId } = useHoverContext();
  const isHovered = hoveredNodeId === id;
  const isTreasury = data.position?.includes("treasury") || data.allocation;
  const isMinimal = data.display === "minimal";
  const badgeUrl = data.icon?.badge ? resolveSpecIconUrl(data.icon.badge) : undefined;

  if (isMinimal) {
    // Core nodes are 2.5x larger than standard nodes
    // Core: 120px, Standard: 48px (40% of core)
    const isCore = data.sizeCategory === "core";
    const baseIconSize = isCore ? 120 : 48;
    const baseFallbackSize = isCore ? 80 : 32;
    const scale = data.showLabel === false ? Math.min(data.scale ?? 1, 1) : 1;
    const iconSize = Math.max(32, Math.round(baseIconSize * scale));
    const fallbackSize = Math.max(24, Math.round(baseFallbackSize * scale));
    const baseBadgeSize = isCore ? 36 : 24;
    const badgeSize = Math.max(12, Math.round(baseBadgeSize * scale));
    const wrapperClass =
      data.showLabel === false
        ? "flex items-center justify-center relative"
        : "flex flex-col items-center gap-2 relative";
    const hiddenHandleClass = "!opacity-0 !w-2 !h-2";
    const handleColor = isTreasury ? "!bg-amber-300" : "!bg-cyan-300";
    return (
      <div
        className={wrapperClass}
        style={data.showLabel === false ? { width: iconSize, height: iconSize } : undefined}
        data-testid={`value-flow-node-internal-${id}`}
      >
        {/* Hover components */}
        <NodeHoverLabel name={data.label} description={data.description} visible={isHovered} />
        <NodeMetadataTooltip
          originalData={data.originalData}
          nodeCategory="internal"
          config={data.config}
          allocation={data.allocation}
          isHovered={isHovered}
        />
        {/* All 4 target handles */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          className={`${hiddenHandleClass} ${handleColor}`}
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right-in"
          className={`${hiddenHandleClass} ${handleColor}`}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom-in"
          className={`${hiddenHandleClass} ${handleColor}`}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left-in"
          className={`${hiddenHandleClass} ${handleColor}`}
        />
        {/* All 4 source handles */}
        <Handle
          type="source"
          position={Position.Top}
          id="top-out"
          className={`${hiddenHandleClass} ${handleColor}`}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          className={`${hiddenHandleClass} ${handleColor}`}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className={`${hiddenHandleClass} ${handleColor}`}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          className={`${hiddenHandleClass} ${handleColor}`}
        />

        <div className="relative flex items-center justify-center">
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="object-contain"
              style={{ width: iconSize, height: iconSize }}
              onError={() => setImageError(true)}
            />
          ) : (
            <div
              className={isTreasury ? "text-sa-warning" : "text-cyan-400"}
              style={{ width: fallbackSize, height: fallbackSize }}
            >
              {getDefaultIcon(data.position)}
            </div>
          )}
          {badgeUrl && !badgeError && (
            <img
              src={badgeUrl}
              alt=""
              className="absolute -bottom-1 -right-1 rounded-full border border-sa-border bg-sa-surface"
              style={{ width: badgeSize, height: badgeSize }}
              onError={() => setBadgeError(true)}
            />
          )}
          {/* Observable badges from schema ui_hint */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size={isCore ? "md" : "sm"}
            />
          )}
        </div>
        {data.showLabel !== false && data.label.trim().length > 0 && (
          <div className="text-base text-sa-text text-center leading-tight max-w-[240px] break-words whitespace-normal font-medium">
            {data.label}
          </div>
        )}
      </div>
    );
  }

  const handleClass = `!w-3 !h-3 ${isTreasury ? "!bg-amber-400 !border-sa-warning" : "!bg-cyan-400 !border-cyan-600"}`;

  return (
    <div
      data-testid={`value-flow-node-internal-${id}`}
      className={`
          px-4 py-3 rounded-lg border-2 min-w-[170px]
          ${
            isTreasury
              ? "bg-gradient-to-br from-sa-warning/10 to-sa-warning/10"
              : "bg-gradient-to-br from-cyan-500/10 to-primary/10"
          }
          ${
            selected
              ? isTreasury
                ? "border-sa-warning shadow-lg shadow-amber-500/30"
                : "border-cyan-400 shadow-lg shadow-cyan-500/30"
              : isTreasury
                ? "border-sa-warning/40"
                : "border-cyan-500/40"
          }
          transition-all duration-200
        `}
    >
      {/* All 4 target handles */}
      <Handle type="target" position={Position.Top} id="top" className={handleClass} />
      <Handle type="target" position={Position.Right} id="right-in" className={handleClass} />
      <Handle type="target" position={Position.Bottom} id="bottom-in" className={handleClass} />
      <Handle type="target" position={Position.Left} id="left-in" className={handleClass} />
      {/* All 4 source handles */}
      <Handle type="source" position={Position.Top} id="top-out" className={handleClass} />
      <Handle type="source" position={Position.Right} id="right" className={handleClass} />
      <Handle type="source" position={Position.Left} id="left" className={handleClass} />

      <div className="flex items-center gap-3">
        {/* Icon */}
        <div
          className={`
              relative flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
              ${isTreasury ? "bg-sa-warning/10 text-sa-warning" : "bg-cyan-500/10 text-cyan-400"}
            `}
        >
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="w-6 h-6 object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            getDefaultIcon(data.position)
          )}
          {badgeUrl && !badgeError && (
            <img
              src={badgeUrl}
              alt=""
              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border border-sa-border bg-sa-surface"
              onError={() => setBadgeError(true)}
            />
          )}
          {/* Observable badges from schema ui_hint */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size="sm"
            />
          )}
        </div>

        {/* Label and allocation */}
        {data.showLabel !== false && (
          <div className="flex-1 min-w-0">
            <div
              className={`text-base font-medium leading-tight break-words whitespace-normal ${
                isTreasury ? "text-sa-warning" : "text-cyan-400"
              }`}
            >
              {data.label}
            </div>
            {data.allocation && (
              <div className="text-sm font-mono text-sa-warning">{data.allocation}</div>
            )}
            {data.description && !data.allocation && (
              <div
                className={`text-sm leading-tight break-words whitespace-normal ${
                  isTreasury ? "text-sa-warning/70" : "text-cyan-400/70"
                }`}
              >
                {data.description}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom source handle */}
      <Handle type="source" position={Position.Bottom} id="bottom" className={handleClass} />
    </div>
  );
});

InternalNodeRF.displayName = "InternalNodeRF";
